import { useMemo } from 'react';
import { useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { CatalogItem, CatalogSuggestion } from '../../../../redux/api/fymAPI.types';
import { defaultClaimWay } from './claimWayRules';
import { ClaimRoute, ClaimWay } from './useClaimRoute';

export interface ClaimWayState {
  /** False with no suggestion, no route or an empty catalog: the dialog claims as before. */
  isOffered: boolean;
  way: ClaimWay;
  /** The row that the release merges into, when the artist picked one (or the match did). */
  intoItem: CatalogItem | undefined;
  items: CatalogItem[];
  setWay: (way: ClaimWay) => void;
  setInto: (uuid: string | undefined) => void;
}

interface UseClaimWayProps {
  accountId: number;
  suggestion: CatalogSuggestion | undefined;
  route: ClaimRoute | undefined;
}

const noop = () => undefined;

/**
 * "Add as a new release" or "Merge into an existing song" (design catalog-merge c2, c3). The way
 * and the picked row come from the address; with none, a suggestion with a match starts on the
 * merge into the matched row.
 */
export function useClaimWay({ accountId, suggestion, route }: UseClaimWayProps): ClaimWayState {
  const isPossible = suggestion !== undefined && route !== undefined;
  const { data } = useGetCatalogQuery({ accountId }, { skip: !isPossible });
  const items = useMemo(() => data?.items ?? [], [data?.items]);

  return useMemo(() => {
    const isOffered = isPossible && items.length > 0;
    const way: ClaimWay = isOffered ? (route?.way ?? defaultClaimWay(suggestion)) : 'new';
    const intoUuid = route?.intoUuid ?? suggestion?.matchesCatalogItem?.id ?? undefined;
    return {
      isOffered,
      way,
      intoItem: items.find((item) => item.uuid === intoUuid),
      items,
      setWay: route?.setWay ?? noop,
      setInto: route?.setInto ?? noop,
    };
  }, [isPossible, items, route, suggestion]);
}
